/**
 * Demo 2 – Container-basierter Erfassungs-Flow Builder
 *
 * Container = "Was wird abgefragt?" (Strecke, Wäscheklasse, etc.)
 * Drag & Drop = "Diese Elemente gehören in den Container"
 * Leerer Container = alle erlaubt · Befüllter Container = nur diese
 *
 * SANDBOX: Keine Backend-Writes, nur lokaler State
 */

import React, { useState, useMemo, useEffect, useCallback, DragEvent } from 'react';
import {
  ChevronUp, ChevronDown, Plus, Trash2, GitBranch, Route, Layers, Package,
  Users, Scale, AlertTriangle, X, Info, Lock, Play, ChevronRight, Search,
  Copy, RotateCcw, Loader2, GripVertical, FlaskConical, Check,
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

// =============================================================================
// TYPES
// =============================================================================

interface SubLine { id: string; name: string; isSandbox?: boolean }
interface ApiLine { id: string; name: string; isActive?: boolean; subLines?: SubLine[]; isSandbox?: boolean }
interface LaundryClassEntity { id: string; code: string; name: string; isActive?: boolean; isSandbox?: boolean }
interface ArticleGroupEntity { id: string; code: string; name: string; isActive?: boolean; isSandbox?: boolean }

type ContainerType = 'LINE' | 'SUB_LINE' | 'LAUNDRY_CLASS' | 'ARTICLE_GROUP' | 'CUSTOMER' | 'WEIGHT';

interface FlowContainer {
  id: string;
  type: ContainerType;
  isActive: boolean;
  items: string[];
  onlyIfExists?: boolean;
}

interface DragItemData {
  id: string;
  name: string;
  code?: string;
  type: ContainerType;
  isSandbox?: boolean;
}

interface ContainerMeta {
  label: string;
  desc: string;
  dropDesc: string;
  icon: React.ElementType;
  isFixed: boolean;
  hasMaster: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const META: Record<ContainerType, ContainerMeta> = {
  LINE:          { label: 'Strecke',       desc: 'Welche Strecke wird bearbeitet?',      dropDesc: 'Strecke auswählen (Kontext)',          icon: GitBranch, isFixed: true,  hasMaster: true  },
  SUB_LINE:      { label: 'Substrecke',    desc: 'Feinere Unterteilung der Strecke',     dropDesc: 'Erlaubte Substrecken hierher ziehen',  icon: Route,     isFixed: false, hasMaster: true  },
  LAUNDRY_CLASS: { label: 'Wäscheklasse',  desc: 'Art der Wäsche (z.B. Hotel, Heim)',    dropDesc: 'Erlaubte Wäscheklassen hierher ziehen', icon: Layers,   isFixed: false, hasMaster: true  },
  ARTICLE_GROUP: { label: 'Artikelgruppe', desc: 'Kategorie der Artikel',                dropDesc: 'Erlaubte Artikelgruppen hierher ziehen', icon: Package, isFixed: false, hasMaster: true  },
  CUSTOMER:      { label: 'Kunde',         desc: 'Für welchen Kunden wird erfasst?',     dropDesc: 'Kunde wird zur Laufzeit ausgewählt',   icon: Users,     isFixed: true,  hasMaster: false },
  WEIGHT:        { label: 'Gewicht',       desc: 'Gewicht der Wäsche in kg',             dropDesc: 'Gewicht wird manuell eingegeben',      icon: Scale,     isFixed: true,  hasMaster: false },
};

const OPTIONAL_TYPES: ContainerType[] = ['SUB_LINE', 'LAUNDRY_CLASS', 'ARTICLE_GROUP'];

const defaultFlow = (): FlowContainer[] => [
  { id: 'c-line',     type: 'LINE',          isActive: true, items: [] },
  { id: 'c-subline',  type: 'SUB_LINE',      isActive: true, items: [], onlyIfExists: true },
  { id: 'c-laundry',  type: 'LAUNDRY_CLASS', isActive: true, items: [] },
  { id: 'c-article',  type: 'ARTICLE_GROUP', isActive: true, items: [] },
  { id: 'c-customer', type: 'CUSTOMER',      isActive: true, items: [] },
  { id: 'c-weight',   type: 'WEIGHT',        isActive: true, items: [] },
];

const genId = () => `sb-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

// =============================================================================
// SMALL COMPONENTS
// =============================================================================

function DraggableItem({ item, onStart }: { item: DragItemData; onStart: (e: DragEvent, i: DragItemData) => void }) {
  const Icon = META[item.type].icon;
  return (
    <div
      draggable
      onDragStart={(e) => onStart(e, item)}
      className="flex items-center gap-2 p-2.5 bg-white border rounded-lg cursor-grab hover:border-blue-300 hover:shadow-sm transition-all group"
    >
      <GripVertical className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
      <Icon className="w-4 h-4 text-gray-500" />
      <span className="flex-1 text-sm font-medium truncate">{item.name}</span>
      {item.code && <span className="text-xs text-gray-400">({item.code})</span>}
      {item.isSandbox && <FlaskConical className="w-3 h-3 text-amber-500" />}
    </div>
  );
}

function Chip({ item, onRemove }: { item: DragItemData; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-sm">
      <span className="text-blue-800 font-medium">{item.name}</span>
      {item.code && <span className="text-blue-500 text-xs">({item.code})</span>}
      {item.isSandbox && <FlaskConical className="w-3 h-3 text-amber-500" />}
      <button onClick={onRemove} className="ml-0.5 p-0.5 hover:bg-blue-100 rounded-full">
        <X className="w-3 h-3 text-blue-500" />
      </button>
    </span>
  );
}

// =============================================================================
// CONTAINER CARD
// =============================================================================

function ContainerCard({ c, idx, sel, onSel, onUp, onDown, onRm, canUp, canDn, onDrop, onRmItem, getItem, hasSub }: {
  c: FlowContainer; idx: number; sel: boolean;
  onSel: () => void; onUp: () => void; onDown: () => void; onRm: () => void;
  canUp: boolean; canDn: boolean;
  onDrop: (i: DragItemData) => void; onRmItem: (id: string) => void;
  getItem: (id: string, t: ContainerType) => DragItemData | undefined;
  hasSub: boolean;
}) {
  const [over, setOver] = useState(false);
  const m = META[c.type];
  const Icon = m.icon;
  const skip = !c.isActive;
  const items = c.items.map((id) => getItem(id, c.type)).filter((x): x is DragItemData => !!x);

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (m.hasMaster && c.type !== 'LINE') setOver(true);
  };
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (!m.hasMaster || c.type === 'LINE') return;
    try {
      const d = JSON.parse(e.dataTransfer.getData('application/json')) as DragItemData;
      if (d.type === c.type) onDrop(d);
    } catch { /* invalid drop */ }
  };

  const badge = () => {
    if (c.type === 'SUB_LINE' && c.onlyIfExists && !hasSub)
      return <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">keine Substrecken</span>;
    if (c.type === 'SUB_LINE' && c.onlyIfExists)
      return <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">wenn vorhanden</span>;
    if (c.items.length > 0)
      return <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">{c.items.length} gewählt</span>;
    if (m.hasMaster && c.type !== 'LINE')
      return <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">alle erlaubt</span>;
    return null;
  };

  return (
    <div
      onClick={onSel}
      onDragOver={handleDragOver}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
      className={[
        'relative rounded-xl border-2 cursor-pointer transition-all',
        skip && 'opacity-50 bg-gray-100 border-dashed border-gray-300',
        !skip && m.isFixed && 'bg-gray-50 border-gray-200',
        !skip && !m.isFixed && sel && 'bg-blue-50 border-blue-500 shadow-lg ring-2 ring-blue-200',
        !skip && !m.isFixed && !sel && 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md',
        over && 'border-blue-500 bg-blue-50 ring-2 ring-blue-200',
      ].filter(Boolean).join(' ')}
    >
      {/* Move/Remove for non-fixed */}
      {!m.isFixed && (
        <div className="absolute -top-3 -right-3 flex gap-1 z-10">
          <button onClick={(e) => { e.stopPropagation(); onUp(); }} disabled={!canUp}
            className={`p-1.5 bg-white border rounded-lg shadow-sm ${canUp ? 'hover:bg-gray-50 text-gray-600' : 'text-gray-300 cursor-not-allowed'}`}>
            <ChevronUp className="w-4 h-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDown(); }} disabled={!canDn}
            className={`p-1.5 bg-white border rounded-lg shadow-sm ${canDn ? 'hover:bg-gray-50 text-gray-600' : 'text-gray-300 cursor-not-allowed'}`}>
            <ChevronDown className="w-4 h-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onRm(); }}
            className="p-1.5 bg-white border rounded-lg shadow-sm hover:bg-red-50 text-red-500">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Index */}
      <div className={`absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${
        skip ? 'bg-gray-300 text-gray-500' : sel ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
      }`}>
        {idx + 1}
      </div>

      {/* Body */}
      <div className="p-4 ml-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center relative flex-shrink-0 ${
            skip ? 'bg-gray-200 text-gray-400'
              : m.isFixed ? 'bg-gray-200 text-gray-500'
              : sel ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-600'
          }`}>
            <Icon className="w-6 h-6" />
            {m.isFixed && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gray-400 rounded-full flex items-center justify-center">
                <Lock className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`font-semibold ${skip ? 'text-gray-400' : 'text-gray-900'}`}>{m.label}</h3>
              {m.isFixed && (
                <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Lock className="w-3 h-3" />System
                </span>
              )}
              {badge()}
            </div>
            <p className={`text-sm mt-0.5 ${skip ? 'text-gray-400' : 'text-gray-500'}`}>{m.desc}</p>

            {/* Drop zone / chips */}
            {m.hasMaster && c.type !== 'LINE' && !skip && (
              <div className="mt-3">
                {items.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {items.map((i) => (
                      <Chip key={i.id} item={i} onRemove={() => onRmItem(i.id)} />
                    ))}
                  </div>
                ) : (
                  <div className={`p-3 border-2 border-dashed rounded-lg text-center text-sm ${
                    over ? 'border-blue-400 bg-blue-50 text-blue-600' : 'border-gray-300 text-gray-400'
                  }`}>
                    {over ? 'Hier ablegen...' : m.dropDesc}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ADD CONTAINER BUTTON
// =============================================================================

function AddBtn({ onAdd, types }: { onAdd: (t: ContainerType) => void; types: ContainerType[] }) {
  const [open, setOpen] = useState(false);

  if (!types.length) {
    return (
      <div className="flex justify-center py-2">
        <div className="w-0.5 h-8 bg-gray-300" />
      </div>
    );
  }

  return (
    <div className="flex justify-center py-2 relative">
      <div className="w-0.5 h-4 bg-gray-300" />
      <button
        onClick={() => setOpen(!open)}
        className="absolute top-1/2 -translate-y-1/2 w-8 h-8 bg-white border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center hover:border-blue-400 hover:bg-blue-50 z-10"
      >
        <Plus className="w-4 h-4 text-gray-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-2 z-30 bg-white rounded-lg shadow-xl border p-2 min-w-[200px]">
            {types.map((t) => {
              const TIcon = META[t].icon;
              return (
                <button
                  key={t}
                  onClick={() => { onAdd(t); setOpen(false); }}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
                >
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                    <TIcon className="w-4 h-4 text-gray-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{META[t].label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
      <div className="w-0.5 h-4 bg-gray-300" />
    </div>
  );
}

// =============================================================================
// SANDBOX CREATE DIALOG
// =============================================================================

function CreateDlg({ type, onClose, onCreate }: {
  type: 'line' | 'subline' | 'laundryClass' | 'articleGroup';
  onClose: () => void;
  onCreate: (d: { name: string; code?: string }) => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const cfg = {
    line:         { title: 'Neue Strecke',       showCode: false },
    subline:      { title: 'Neue Substrecke',    showCode: false },
    laundryClass: { title: 'Neue Wäscheklasse',  showCode: true  },
    articleGroup: { title: 'Neue Artikelgruppe',  showCode: true  },
  }[type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          onCreate({ name: name.trim(), code: code.trim() || undefined });
          onClose();
        }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-5 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-gray-900">{cfg.title} (Sandbox)</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Test"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>

        {cfg.showCode && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code (optional)</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="z.B. TST"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0" />
          Nur lokal gespeichert (Sandbox)
        </div>

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            Abbrechen
          </button>
          <button type="submit" disabled={!name.trim()} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            Erstellen
          </button>
        </div>
      </form>
    </div>
  );
}

// =============================================================================
// LEFT PANEL (eigene Komponente wegen eigenem useState)
// =============================================================================

function LeftPanel({ selCType, allLines, allSub, allLC, allAG, selLineId, setSelLineId, selLine,
  onDragStart, setDlg, loading }: {
  selCType: ContainerType | null;
  allLines: ApiLine[]; allSub: SubLine[]; allLC: LaundryClassEntity[]; allAG: ArticleGroupEntity[];
  selLineId: string | null; setSelLineId: (id: string | null) => void; selLine: ApiLine | undefined;
  onDragStart: (e: DragEvent, i: DragItemData) => void;
  setDlg: (t: 'line' | 'subline' | 'laundryClass' | 'articleGroup') => void;
  loading: boolean;
}) {
  const [search, setSearch] = useState('');

  // Reset search when container type changes
  useEffect(() => { setSearch(''); }, [selCType]);

  if (!selCType) {
    return (
      <div className="p-6 text-center text-gray-500">
        <Layers className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p className="text-sm font-medium">Kein Container ausgewählt</p>
        <p className="text-xs mt-1">Klicke auf einen Container in der Mitte</p>
      </div>
    );
  }

  const m = META[selCType];

  // No master data (Customer / Weight)
  if (!m.hasMaster) {
    const MIcon = m.icon;
    return (
      <div className="p-6">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">{m.label}</h2>
        <div className="p-4 bg-gray-50 rounded-lg text-center">
          <MIcon className="w-10 h-10 mx-auto mb-3 text-gray-400" />
          <p className="text-sm text-gray-600">
            {selCType === 'CUSTOMER'
              ? 'Kunden werden zur Laufzeit aus dem System geladen.'
              : 'Das Gewicht wird vom Vorarbeiter manuell eingegeben.'}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Dieser Container bestimmt nur, ob {m.label} im Ablauf abgefragt wird.
          </p>
        </div>
      </div>
    );
  }

  const searchInput = (
    <div className="relative mb-3">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Suchen..."
        className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );

  const sandboxBtn = (label: string, type: 'line' | 'subline' | 'laundryClass' | 'articleGroup') => (
    <button
      onClick={() => setDlg(type)}
      className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100"
    >
      <Plus className="w-4 h-4" />
      {label} (Sandbox)
    </button>
  );

  // LINE
  if (selCType === 'LINE') {
    const flt = allLines.filter((l) => l.name.toLowerCase().includes(search.toLowerCase()));
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Strecken</h2>
          {searchInput}
          {sandboxBtn('Neue Strecke', 'line')}
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="p-4 flex flex-col items-center text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <span className="text-sm">Lade...</span>
            </div>
          ) : flt.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">Keine Strecken</div>
          ) : (
            <div className="space-y-2">
              {flt.map((l) => (
                <div
                  key={l.id}
                  onClick={() => setSelLineId(l.id)}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    selLineId === l.id
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-white border-2 border-transparent hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <GitBranch className={`w-4 h-4 ${selLineId === l.id ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span className="flex-1 text-sm font-medium truncate">{l.name}</span>
                    {l.isSandbox && <FlaskConical className="w-3.5 h-3.5 text-amber-500" />}
                  </div>
                  {(l.subLines?.length || 0) > 0 && (
                    <p className="text-xs text-gray-500 mt-1 ml-6">{l.subLines?.length} Substrecken</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // SUB_LINE
  if (selCType === 'SUB_LINE') {
    if (!selLineId) {
      return (
        <div className="p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Substrecken</h2>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
            <Info className="w-8 h-8 mx-auto mb-2 text-amber-500" />
            <p className="text-sm text-amber-700 font-medium">Bitte zuerst eine Strecke auswählen</p>
            <p className="text-xs text-amber-600 mt-1">Klicke auf den Strecken-Container</p>
          </div>
        </div>
      );
    }
    const flt = allSub.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()));
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">Substrecken</h2>
          <p className="text-xs text-gray-500 mb-3">für: {selLine?.name}</p>
          {searchInput}
          {sandboxBtn('Neue Substrecke', 'subline')}
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {flt.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">Keine Substrecken</div>
          ) : (
            <div className="space-y-2">
              {flt.map((s) => (
                <DraggableItem
                  key={s.id}
                  item={{ id: s.id, name: s.name, type: 'SUB_LINE', isSandbox: s.isSandbox }}
                  onStart={onDragStart}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // LAUNDRY_CLASS
  if (selCType === 'LAUNDRY_CLASS') {
    const flt = allLC.filter((x) =>
      x.name.toLowerCase().includes(search.toLowerCase()) ||
      x.code.toLowerCase().includes(search.toLowerCase())
    );
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Wäscheklassen</h2>
          {searchInput}
          {sandboxBtn('Neue Wäscheklasse', 'laundryClass')}
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {flt.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">Keine Wäscheklassen</div>
          ) : (
            <div className="space-y-2">
              {flt.map((x) => (
                <DraggableItem
                  key={x.id}
                  item={{ id: x.id, name: x.name, code: x.code, type: 'LAUNDRY_CLASS', isSandbox: x.isSandbox }}
                  onStart={onDragStart}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ARTICLE_GROUP
  if (selCType === 'ARTICLE_GROUP') {
    const flt = allAG.filter((x) =>
      x.name.toLowerCase().includes(search.toLowerCase()) ||
      x.code.toLowerCase().includes(search.toLowerCase())
    );
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Artikelgruppen</h2>
          {searchInput}
          {sandboxBtn('Neue Artikelgruppe', 'articleGroup')}
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {flt.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">Keine Artikelgruppen</div>
          ) : (
            <div className="space-y-2">
              {flt.map((x) => (
                <DraggableItem
                  key={x.id}
                  item={{ id: x.id, name: x.name, code: x.code, type: 'ARTICLE_GROUP', isSandbox: x.isSandbox }}
                  onStart={onDragStart}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

// =============================================================================
// RIGHT PANEL
// =============================================================================

function RightPanel({ selC, updateC, flow, hasSub, getItem }: {
  selC: FlowContainer | null;
  updateC: (id: string, u: Partial<FlowContainer>) => void;
  flow: FlowContainer[];
  hasSub: boolean;
  getItem: (id: string, t: ContainerType) => DragItemData | undefined;
}) {
  const activeFlow = flow.filter((c) => {
    if (!c.isActive) return false;
    if (c.type === 'SUB_LINE' && c.onlyIfExists && !hasSub) return false;
    return true;
  });

  return (
    <div className="flex flex-col h-full">
      {/* Settings */}
      <div className="p-4 border-b">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Einstellungen</h2>
        {selC ? (() => {
          const m = META[selC.type];
          const SIcon = m.icon;
          return (
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <SIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{m.label}</h3>
                  <p className="text-xs text-gray-500">{m.desc}</p>
                </div>
              </div>

              {/* Active toggle */}
              {!m.isFixed && (
                <div className="p-3 bg-gray-50 rounded-lg mb-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Container aktiv</span>
                    <div className={`relative w-11 h-6 rounded-full transition-colors ${selC.isActive ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      <input type="checkbox" checked={selC.isActive}
                        onChange={(e) => updateC(selC.id, { isActive: e.target.checked })}
                        className="sr-only" />
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${selC.isActive ? 'translate-x-5' : ''}`} />
                    </div>
                  </label>
                </div>
              )}

              {/* SUB_LINE toggle */}
              {selC.type === 'SUB_LINE' && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 mb-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-medium text-purple-900">Nur wenn Substrecken existieren</span>
                      <p className="text-xs text-purple-600 mt-0.5">Container wird übersprungen wenn keine Substrecken</p>
                    </div>
                    <div className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ml-3 ${selC.onlyIfExists ? 'bg-purple-500' : 'bg-gray-300'}`}>
                      <input type="checkbox" checked={selC.onlyIfExists ?? true}
                        onChange={(e) => updateC(selC.id, { onlyIfExists: e.target.checked })}
                        className="sr-only" />
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${selC.onlyIfExists ? 'translate-x-5' : ''}`} />
                    </div>
                  </label>
                </div>
              )}

              {/* Item count info */}
              {m.hasMaster && selC.type !== 'LINE' && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700">
                    {selC.items.length === 0
                      ? 'Alle Elemente erlaubt (Container leer)'
                      : `Nur ${selC.items.length} Element(e) erlaubt`}
                  </p>
                </div>
              )}
            </div>
          );
        })() : (
          <div className="p-4 bg-white rounded-xl border text-center text-gray-500">
            <Layers className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Wähle einen Container</p>
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="flex-1 p-4 overflow-y-auto">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
          So sieht's der Vorarbeiter
        </h3>
        <div className="bg-white rounded-xl border p-4">
          <div className="space-y-2">
            {activeFlow.map((c, i, arr) => {
              const cm = META[c.type];
              const CIcon = cm.icon;
              let lbl = cm.label;
              if (cm.hasMaster && c.type !== 'LINE') {
                lbl += c.items.length === 0 ? ' (alle)' : ` (${c.items.length} ausgewählt)`;
              }
              if (c.type === 'SUB_LINE' && c.onlyIfExists) {
                lbl += ' (wenn vorhanden)';
              }
              return (
                <div key={c.id} className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <CIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{lbl}</span>
                  {i < arr.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 ml-auto" />}
                </div>
              );
            })}
          </div>
          <button className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
            <Play className="w-4 h-4" />
            Simulation starten
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export default function CaptureFlowBuilderDemo2Page() {
  // API data (READ-ONLY)
  const [apiLines, setApiLines] = useState<ApiLine[]>([]);
  const [apiLC, setApiLC] = useState<LaundryClassEntity[]>([]);
  const [apiAG, setApiAG] = useState<ArticleGroupEntity[]>([]);
  const [loading, setLoading] = useState(true);

  // Sandbox data (LOCAL ONLY)
  const [sbLines, setSbLines] = useState<ApiLine[]>([]);
  const [sbLC, setSbLC] = useState<LaundryClassEntity[]>([]);
  const [sbAG, setSbAG] = useState<ArticleGroupEntity[]>([]);
  const [sbSub, setSbSub] = useState<Record<string, SubLine[]>>({});

  // Flow
  const [flow, setFlow] = useState<FlowContainer[]>(defaultFlow());
  const [selCId, setSelCId] = useState<string | null>(null);
  const [selLineId, setSelLineId] = useState<string | null>(null);

  // UI
  const [toast, setToast] = useState(false);
  const [dlg, setDlg] = useState<'line' | 'subline' | 'laundryClass' | 'articleGroup' | null>(null);

  // Load API data
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [l, lc, ag] = await Promise.all([
          fetch('/api/data/lines', { credentials: 'include' }),
          fetch('/api/data/laundry-classes', { credentials: 'include' }),
          fetch('/api/data/article-groups', { credentials: 'include' }),
        ]);
        if (l.ok) setApiLines(await l.json());
        if (lc.ok) setApiLC((await lc.json()).filter((x: LaundryClassEntity) => x.isActive !== false));
        if (ag.ok) setApiAG((await ag.json()).filter((x: ArticleGroupEntity) => x.isActive !== false));
      } catch (e) {
        console.error('Fehler beim Laden:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Derived
  const selC = flow.find((c) => c.id === selCId) || null;
  const selCType = selC?.type || null;
  const allLines = useMemo(() => [...apiLines, ...sbLines], [apiLines, sbLines]);
  const selLine = allLines.find((l) => l.id === selLineId);
  const hasSub = (selLine?.subLines?.length || 0) + (sbSub[selLineId || '']?.length || 0) > 0;
  const allLC = useMemo(() => [...apiLC, ...sbLC], [apiLC, sbLC]);
  const allAG = useMemo(() => [...apiAG, ...sbAG], [apiAG, sbAG]);
  const allSub = useMemo(
    () => selLineId ? [...(selLine?.subLines || []), ...(sbSub[selLineId] || [])] : [],
    [selLineId, selLine, sbSub],
  );

  // Item lookup
  const getItem = useCallback((id: string, t: ContainerType): DragItemData | undefined => {
    if (t === 'LAUNDRY_CLASS') {
      const x = allLC.find((y) => y.id === id);
      return x ? { id: x.id, name: x.name, code: x.code, type: t, isSandbox: x.isSandbox } : undefined;
    }
    if (t === 'ARTICLE_GROUP') {
      const x = allAG.find((y) => y.id === id);
      return x ? { id: x.id, name: x.name, code: x.code, type: t, isSandbox: x.isSandbox } : undefined;
    }
    if (t === 'SUB_LINE') {
      const x = allSub.find((y) => y.id === id);
      return x ? { id: x.id, name: x.name, type: t, isSandbox: x.isSandbox } : undefined;
    }
    return undefined;
  }, [allLC, allAG, allSub]);

  // Drag & Drop
  const onDragStart = useCallback((e: DragEvent, item: DragItemData) => {
    e.dataTransfer.setData('application/json', JSON.stringify(item));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  // Container ops
  const updateC = useCallback((cid: string, u: Partial<FlowContainer>) => {
    setFlow((f) => f.map((c) => c.id === cid ? { ...c, ...u } : c));
  }, []);

  const addItem = useCallback((cid: string, item: DragItemData) => {
    setFlow((f) => f.map((c) =>
      c.id === cid && !c.items.includes(item.id) ? { ...c, items: [...c.items, item.id] } : c
    ));
  }, []);

  const rmItem = useCallback((cid: string, itemId: string) => {
    setFlow((f) => f.map((c) =>
      c.id === cid ? { ...c, items: c.items.filter((i) => i !== itemId) } : c
    ));
  }, []);

  const avail = useCallback((pos: number) => {
    const ex = new Set(flow.map((c) => c.type));
    return OPTIONAL_TYPES.filter((t) => {
      if (ex.has(t)) return false;
      if (t === 'SUB_LINE' && pos !== flow.findIndex((c) => c.type === 'LINE') + 1) return false;
      return true;
    });
  }, [flow]);

  const addC = useCallback((t: ContainerType, after: number) => {
    const n: FlowContainer = {
      id: `c-${t.toLowerCase()}-${Date.now()}`,
      type: t, isActive: true, items: [],
      onlyIfExists: t === 'SUB_LINE' ? true : undefined,
    };
    const f = [...flow];
    f.splice(after + 1, 0, n);
    setFlow(f);
    setSelCId(n.id);
  }, [flow]);

  const rmC = useCallback((cid: string) => {
    const c = flow.find((x) => x.id === cid);
    if (!c || META[c.type].isFixed) return;
    setFlow(flow.filter((x) => x.id !== cid));
    if (selCId === cid) setSelCId(null);
  }, [flow, selCId]);

  const moveC = useCallback((i: number, d: 'up' | 'down') => {
    const c = flow[i];
    if (!c || META[c.type].isFixed) return;
    const ni = d === 'up' ? i - 1 : i + 1;
    if (ni < 0 || ni >= flow.length || META[flow[ni].type].isFixed) return;
    const f = [...flow];
    [f[i], f[ni]] = [f[ni], f[i]];
    setFlow(f);
  }, [flow]);

  const canUp = (i: number) => i > 0 && flow[i] && !META[flow[i].type].isFixed && !META[flow[i - 1].type].isFixed;
  const canDn = (i: number) => i < flow.length - 1 && flow[i] && !META[flow[i].type].isFixed && !META[flow[i + 1].type].isFixed;

  // Sandbox create
  const onCreate = useCallback((d: { name: string; code?: string }) => {
    if (!dlg) return;
    const id = genId();
    if (dlg === 'line') {
      setSbLines((p) => [...p, { id, name: d.name, isActive: true, isSandbox: true, subLines: [] }]);
    } else if (dlg === 'subline' && selLineId) {
      setSbSub((p) => ({ ...p, [selLineId]: [...(p[selLineId] || []), { id, name: d.name, isSandbox: true }] }));
    } else if (dlg === 'laundryClass') {
      setSbLC((p) => [...p, { id, name: d.name, code: d.code || d.name.substring(0, 3).toUpperCase(), isActive: true, isSandbox: true }]);
    } else if (dlg === 'articleGroup') {
      setSbAG((p) => [...p, { id, name: d.name, code: d.code || d.name.substring(0, 3).toUpperCase(), isActive: true, isSandbox: true }]);
    }
  }, [dlg, selLineId]);

  // Actions
  const copyJson = () => {
    const data = { flow, selLineId, sandbox: { lines: sbLines, lc: sbLC, ag: sbAG, sub: sbSub } };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setToast(true);
    setTimeout(() => setToast(false), 2000);
  };

  const reset = () => {
    setFlow(defaultFlow());
    setSelCId(null);
    setSbLines([]);
    setSbLC([]);
    setSbAG([]);
    setSbSub({});
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                Demo 2 – Container Flow Builder
                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded-full border border-amber-300">
                  SANDBOX
                </span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Container = Was wird abgefragt? · Drag & Drop = Welche Elemente sind erlaubt?
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={copyJson}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border rounded-lg hover:bg-gray-50">
                <Copy className="w-4 h-4" />JSON exportieren
              </button>
              <button onClick={reset}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-amber-700 bg-white border border-amber-300 rounded-lg hover:bg-amber-50">
                <RotateCcw className="w-4 h-4" />Zurücksetzen
              </button>
              <div className="flex items-center gap-2 bg-white border border-amber-300 rounded-lg px-4 py-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="text-sm text-amber-800 font-medium">Nicht speicherbar</span>
              </div>
            </div>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className="fixed top-4 right-4 z-50 px-4 py-2 bg-green-600 text-white rounded-lg shadow-lg flex items-center gap-2">
            <Check className="w-4 h-4" />JSON kopiert!
          </div>
        )}

        {/* 3-Column Layout */}
        <div className="flex h-[calc(100vh-88px)]">
          {/* LEFT: Draggable Elements */}
          <div className="w-72 bg-white border-r flex flex-col">
            <div className="p-3 border-b bg-gray-50 text-xs text-gray-600 flex items-center gap-2">
              <GripVertical className="w-3.5 h-3.5" />
              Elemente in Container ziehen
            </div>
            <div className="flex-1 overflow-hidden">
              <LeftPanel
                selCType={selCType}
                allLines={allLines}
                allSub={allSub}
                allLC={allLC}
                allAG={allAG}
                selLineId={selLineId}
                setSelLineId={setSelLineId}
                selLine={selLine}
                onDragStart={onDragStart}
                setDlg={setDlg}
                loading={loading}
              />
            </div>
          </div>

          {/* MIDDLE: Container Flow */}
          <div className="flex-1 p-8 overflow-y-auto">
            <div className="max-w-xl mx-auto">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Erfassungs-Ablauf
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Klicke Container für Details · Ziehe Elemente von links in Container
              </p>
              <div className="space-y-0">
                {flow.map((c, i) => {
                  const av = avail(i + 1);
                  const isLast = i === flow.length - 1;
                  return (
                    <React.Fragment key={c.id}>
                      <ContainerCard
                        c={c} idx={i} sel={selCId === c.id}
                        onSel={() => setSelCId(c.id === selCId ? null : c.id)}
                        onUp={() => moveC(i, 'up')}
                        onDown={() => moveC(i, 'down')}
                        onRm={() => rmC(c.id)}
                        canUp={canUp(i)} canDn={canDn(i)}
                        onDrop={(item) => addItem(c.id, item)}
                        onRmItem={(itemId) => rmItem(c.id, itemId)}
                        getItem={getItem} hasSub={hasSub}
                      />
                      {!isLast && <AddBtn onAdd={(t) => addC(t, i)} types={av} />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT: Settings + Preview */}
          <div className="w-80 bg-gray-50 border-l">
            <RightPanel
              selC={selC}
              updateC={updateC}
              flow={flow}
              hasSub={hasSub}
              getItem={getItem}
            />
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      {dlg && <CreateDlg type={dlg} onClose={() => setDlg(null)} onCreate={onCreate} />}
    </AdminLayout>
  );
}
